import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `home` in Brazilian Portuguese. Declared `: Messages['home']`, never inferred (LOC-011).
 *
 * Two entries here are rewritten rather than translated, and both for grammar rather than
 * taste:
 *
 *   - **The greeting.** English fills an unknown name with "there" — *Good morning, there*.
 *     Portuguese has no such word, and every candidate (*olá*, *pessoa*, *você*) reads as
 *     either a second greeting or an address to a stranger. So when the name is absent the
 *     address is dropped and the greeting stands alone: *Bom dia!* That is only expressible
 *     because what crosses the boundary is `null` and not a noun English already chose.
 *   - **The privacy line.** English appends a fixed "shared with you" after a pluralised
 *     noun. Portuguese has to agree that participle with *agente* (m.), so the whole clause
 *     is plural-formed — *agente compartilhado* / *agentes compartilhados* — instead of a
 *     noun with a tail bolted on afterwards. Same signature, same sentence, different seam.
 *
 * Register is `você`, informal-professional (LOC-061), and the assistant is never named in
 * the tile bodies: *assistente* is the product, and *agente* is the ratified word for what
 * this screen is counting (ADR-0018).
 */

/** The same slice the English file takes, so the two signatures are interchangeable. */
type Counting = { locale: Locale; formatCount: (value: number) => string }

/** `Intl.PluralRules`, never `count === 1` — Portuguese puts ZERO in the singular category. */
const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

export const home: Messages['home'] = {
  greeting: (hour, firstName) => {
    const salutation = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite'
    // No name to address: the greeting stands on its own rather than borrowing a word for a
    // person Portuguese has no way to name.
    return firstName === null ? `${salutation}!` : `${salutation}, ${firstName}`
  },
  standfirst: 'Pergunte o que quiser. Toda resposta mostra os trechos que usou, quanto custou e o que fez.',

  composerLabel: 'Faça uma pergunta',
  composerPlaceholder: 'O que você quer saber?',
  ask: 'Perguntar',
  asking: 'Começando…',

  tiles: {
    // "Agentes", not "Assistentes": the English body glosses the title with a second word,
    // and reusing that gloss here would spend the one noun this product reserves for itself
    // (ADR-0018 bans *assistente* for *agent* precisely because it is already taken).
    agents: { title: 'Agentes', body: 'Os agentes compartilhados com você. Cada um diz o que sabe.' },
    knowledge: { title: 'Conhecimento', body: 'Documentos que você pode ler, e de onde vêm as respostas.' },
    // "O que ficou guardado" keeps the sentence subject impersonal. English says "it
    // remembers", and the only Portuguese pronoun for that *it* would be *ele* — which reads
    // as the assistant, on the one tile that is explicitly about the person's own data.
    memory: { title: 'Memória', body: 'O que ficou guardado sobre como você trabalha. Você pode alterar ou remover.' },
    approvals: { title: 'Aprovações', body: 'Esperando por você antes que qualquer coisa aconteça.' },
    activity: { title: 'Atividade', body: 'Cada execução, quanto custou e as fontes que usou. Últimos 30 dias.' }
  },

  privacyLine: (fmt, bases, agents) => {
    if (bases === null || agents === null) return 'nada sai da sua rede'
    const baseWord = plural(fmt, bases, { one: 'base de conhecimento', other: 'bases de conhecimento' })
    // The participle travels inside the plural form because it agrees with *agente* (m.).
    const agentPhrase = plural(fmt, agents, {
      one: 'agente compartilhado com você',
      other: 'agentes compartilhados com você'
    })
    return `${fmt.formatCount(bases)} ${baseWord} · ${fmt.formatCount(agents)} ${agentPhrase} · nada sai da sua rede`
  }
}
